<script>
  /**
   * Pantalla de cuentas.
   *
   * Agrupa por naturaleza, no por orden alfabético: primero lo que TIENES,
   * después lo que DEBES. Es el orden en que uno piensa en su plata, y hace
   * que el patrimonio neto se entienda sin explicar nada.
   */
  import { db, TIPO_CUENTA, CUENTAS_PASIVO, CUENTAS_CON_CUPO, crearCuenta, crearTarjeta } from '../lib/db.js';
  import { formatear, formatearAria, interpretar } from '../lib/dinero.js';
  import { guardar, borrar } from '../lib/repositorio.js';
  import LogoEntidad from './LogoEntidad.svelte';

  let { abierta = $bindable(false), cuentas = [], saldos = {}, entidades = [] } = $props();

  let agregando = $state(false);
  let paso = $state(1);

  // Lo que se va armando
  let tipo = $state(TIPO_CUENTA.AHORROS);
  let entidadId = $state(null);
  let nombre = $state('');
  let saldoTexto = $state('');
  let cupoTexto = $state('');
  let diaCorte = $state(15);
  let diaLimite = $state(5);
  let exentaGmf = $state(false);
  let guardando = $state(false);
  let porBorrar = $state(null);
  let movimientosDeLaCuenta = $state(0);

  /**
   * Antes de borrar una cuenta hay que saber cuantos movimientos cuelgan de
   * ella. Borrar una cuenta con historia deja esos movimientos apuntando al
   * vacio y el patrimonio deja de cuadrar.
   */
  async function pedirBorrado(cuenta) {
    porBorrar = cuenta;
    const [origen, destino] = await Promise.all([
      db.movimientos.where('cuenta_origen').equals(cuenta.id).count(),
      db.movimientos.where('cuenta_destino').equals(cuenta.id).count(),
    ]);
    movimientosDeLaCuenta = origen + destino;
  }

  /** Archivar la esconde pero conserva su historia. Es lo recomendable. */
  async function archivar() {
    await guardar('cuentas', { ...porBorrar, archivada: true });
    porBorrar = null;
  }

  async function borrarCuenta() {
    await borrar('cuentas', porBorrar.id);
    porBorrar = null;
  }

  const GRUPOS = [
    {
      titulo: 'Lo que tienes',
      tipos: [TIPO_CUENTA.EFECTIVO, TIPO_CUENTA.AHORROS, TIPO_CUENTA.CORRIENTE,
              TIPO_CUENTA.BILLETERA, TIPO_CUENTA.INVERSION, TIPO_CUENTA.POR_COBRAR],
    },
    {
      titulo: 'Lo que debes',
      tipos: [TIPO_CUENTA.TARJETA_CREDITO, TIPO_CUENTA.CREDITO_ROTATIVO,
              TIPO_CUENTA.PRESTAMO, TIPO_CUENTA.POR_PAGAR],
    },
  ];

  const NOMBRE_TIPO = {
    efectivo: 'Efectivo',
    ahorros: 'Cuenta de ahorros',
    corriente: 'Cuenta corriente',
    billetera: 'Billetera digital',
    tarjeta_credito: 'Tarjeta de crédito',
    credito_rotativo: 'Crédito por compra',
    prestamo: 'Préstamo',
    inversion: 'Inversión',
    por_cobrar: 'Me deben',
    por_pagar: 'Debo a alguien',
  };

  const AYUDA_TIPO = {
    efectivo: 'La plata del bolsillo',
    ahorros: 'Bancolombia, Banco de Bogotá, Davivienda…',
    corriente: 'Cuenta corriente de banco',
    billetera: 'Nequi, Daviplata, Movii, RappiPay',
    tarjeta_credito: 'Nu, Visa, Mastercard. Con fecha de corte y de pago',
    credito_rotativo: 'Addi, Sistecrédito. Cada compra con sus propias cuotas',
    prestamo: 'Crédito de libre inversión, libranza',
    inversion: 'CDT, fondo, acciones, cripto',
    por_cobrar: 'Plata que te deben',
    por_pagar: 'Fiado, plata que le debes a alguien',
  };

  const entidadesPorId = $derived(Object.fromEntries(entidades.map((e) => [e.id, e])));

  /** Solo las entidades que tienen sentido para el tipo elegido. */
  const entidadesSugeridas = $derived.by(() => {
    if (tipo === TIPO_CUENTA.EFECTIVO) return [];
    const filtro = {
      billetera: ['billetera'],
      tarjeta_credito: ['banco', 'neobanco'],
      credito_rotativo: ['credito'],
      ahorros: ['banco', 'neobanco', 'cooperativa'],
      corriente: ['banco'],
    }[tipo];
    if (!filtro) return entidades;
    return entidades.filter((e) => filtro.includes(e.tipo));
  });

  const esTarjeta = $derived(tipo === TIPO_CUENTA.TARJETA_CREDITO);
  const tieneCupo = $derived(CUENTAS_CON_CUPO.includes(tipo));
  const monto = $derived(interpretar(saldoTexto) ?? 0);
  const cupo = $derived(interpretar(cupoTexto) ?? 0);

  function porGrupo(tipos) {
    return cuentas.filter((c) => tipos.includes(c.tipo) && !c.archivada);
  }

  function elegirTipo(t) {
    tipo = t;
    entidadId = null;
    nombre = '';
    paso = 2;
  }

  function elegirEntidad(e) {
    entidadId = e?.id ?? null;
    // El nombre se propone solo: en el 90% de los casos "Nequi" o
    // "Bancolombia" es exactamente lo que la persona iba a escribir.
    if (e && !nombre) nombre = e.nombre;
    paso = 3;
  }

  function reiniciar() {
    agregando = false;
    paso = 1;
    tipo = TIPO_CUENTA.AHORROS;
    entidadId = null;
    nombre = '';
    saldoTexto = '';
    cupoTexto = '';
    exentaGmf = false;
    guardando = false;
  }

  async function confirmar() {
    if (!nombre.trim() || guardando) return;
    guardando = true;
    try {
      const ent = entidadesPorId[entidadId];
      const cuenta = crearCuenta({
        nombre: nombre.trim(),
        tipo,
        entidad_id: entidadId,
        // En una tarjeta o un crédito, lo que se debe se guarda como saldo
        // negativo: es un pasivo, resta del patrimonio.
        saldo_inicial: CUENTAS_PASIVO.includes(tipo) ? -Math.abs(monto) : monto,
        cupo_total: tieneCupo ? cupo : 0,
        exenta_gmf: exentaGmf,
        color: ent?.color ?? null,
        icono: ICONOS[tipo] ?? null,
        orden: cuentas.length,
      });
      await guardar('cuentas', cuenta);

      // Una tarjeta de crédito lleva además su configuración de fechas, que es
      // lo que permite calcular cuánto toca pagar y cuándo.
      if (esTarjeta) {
        await guardar('tarjetas', crearTarjeta({
          cuenta_id: cuenta.id,
          banco: ent?.nombre ?? nombre.trim(),
          dia_corte: Number(diaCorte),
          dia_limite_pago: Number(diaLimite),
        }));
      }
      reiniciar();
    } finally {
      guardando = false;
    }
  }

  const ICONOS = {
    efectivo: '💵', ahorros: '🏦', corriente: '🏦', billetera: '📱',
    tarjeta_credito: '💳', credito_rotativo: '🧾', prestamo: '📄',
    inversion: '📈', por_cobrar: '🤝', por_pagar: '✋',
  };
</script>

{#if abierta}
  <div class="pantalla">
    <header>
      <button class="volver" onclick={() => (abierta = false)} aria-label="Volver">←</button>
      <h1>Cuentas</h1>
      {#if !agregando}
        <button class="agregar" onclick={() => (agregando = true)}>+ Agregar</button>
      {/if}
    </header>

    {#if !agregando}
      <div class="contenido">
        {#each GRUPOS as grupo}
          {@const delGrupo = porGrupo(grupo.tipos)}
          {#if delGrupo.length}
            <section>
              <h2>{grupo.titulo}</h2>
              {#each delGrupo as c (c.id)}
                {@const saldo = saldos[c.id] ?? 0}
                {@const esDeuda = CUENTAS_PASIVO.includes(c.tipo)}
                <button class="cuenta" onclick={() => pedirBorrado(c)}>
                  <LogoEntidad entidad={entidadesPorId[c.entidad_id]} icono={c.icono} />
                  <div class="datos">
                    <p class="nombre">{c.nombre}</p>
                    <p class="tipo">
                      {NOMBRE_TIPO[c.tipo] ?? c.tipo}
                      {#if c.exenta_gmf}<span class="marca">exenta 4x1000</span>{/if}
                    </p>
                  </div>
                  <div class="cifras">
                    <span class="cifra saldo" class:deuda={esDeuda} aria-hidden="true">
                      {formatear(esDeuda ? -Math.abs(saldo) : saldo)}
                    </span>
                    <span class="solo-lectores">{formatearAria(saldo)}</span>
                    {#if c.cupo_total > 0}
                      <span class="cupo cifra">
                        de {formatear(c.cupo_total, { simbolo: false })} de cupo
                      </span>
                    {/if}
                  </div>
                </button>
              {/each}
            </section>
          {/if}
        {/each}

        {#if porBorrar}
          <div class="dialogo" role="dialog" aria-modal="true">
            <h3>{porBorrar.nombre}</h3>
            {#if movimientosDeLaCuenta > 0}
              <p class="explica">
                Tiene <strong>{movimientosDeLaCuenta} movimientos</strong> asociados.
                Si la borras, esos movimientos quedan sueltos y el patrimonio
                deja de cuadrar.
              </p>
              <p class="explica">
                <strong>Archivarla</strong> la esconde de la lista pero conserva
                toda su historia. Es lo que conviene casi siempre.
              </p>
            {:else}
              <p class="explica">No tiene movimientos, se puede borrar sin problema.</p>
            {/if}
            <div class="botones">
              <button class="secundario" onclick={() => (porBorrar = null)}>Cancelar</button>
              {#if movimientosDeLaCuenta > 0}
                <button class="principal" onclick={archivar}>Archivar</button>
              {/if}
              <button class="peligroso" onclick={borrarCuenta}>Borrar</button>
            </div>
          </div>
        {/if}

        {#if cuentas.length <= 2}
          <p class="sugerencia">
            Agrega tus cuentas reales: Nequi, Bancolombia, Banco de Bogotá,
            tu tarjeta de Nu, Addi. Entre más completo, más sirven los números.
          </p>
        {/if}
      </div>

    <!-- ── Agregar: tres pasos, uno por pantalla ── -->
    {:else}
      <div class="contenido">
        {#if paso === 1}
          <h2>¿Qué vas a agregar?</h2>
          <div class="lista-opciones">
            {#each Object.entries(NOMBRE_TIPO) as [valor, etiqueta]}
              <button class="opcion" onclick={() => elegirTipo(valor)}>
                <span class="icono" aria-hidden="true">{ICONOS[valor]}</span>
                <span class="texto-opcion">
                  <span class="titulo-opcion">{etiqueta}</span>
                  <span class="ayuda-opcion">{AYUDA_TIPO[valor]}</span>
                </span>
              </button>
            {/each}
          </div>

        {:else if paso === 2}
          <h2>¿De qué entidad?</h2>
          <div class="lista-opciones">
            {#each entidadesSugeridas as e (e.id)}
              <button class="opcion" onclick={() => elegirEntidad(e)}>
                <LogoEntidad entidad={e} tamano={32} />
                <span class="titulo-opcion">{e.nombre}</span>
              </button>
            {/each}
            <button class="opcion" onclick={() => elegirEntidad(null)}>
              <span class="punto-color" style="background:var(--ink-3)"></span>
              <span class="titulo-opcion">Otra / ninguna</span>
            </button>
          </div>

        {:else}
          <h2>Los datos</h2>

          <label>
            <span class="etiqueta">Nombre</span>
            <input type="text" bind:value={nombre} placeholder="Como la llamas tú" />
          </label>

          <label>
            <span class="etiqueta">
              {CUENTAS_PASIVO.includes(tipo) ? '¿Cuánto debes hoy?' : '¿Cuánto tienes hoy?'}
            </span>
            <input
              type="text"
              inputmode="numeric"
              bind:value={saldoTexto}
              placeholder="0"
            />
            <span class="ayuda">
              Puedes escribir "250 mil" o "1,5 millones".
              {#if monto > 0}<strong>Quedaría en {formatear(monto)}.</strong>{/if}
            </span>
          </label>

          {#if tieneCupo}
            <label>
              <span class="etiqueta">Cupo total</span>
              <input type="text" inputmode="numeric" bind:value={cupoTexto} placeholder="0" />
              <span class="ayuda">El máximo que te prestan.</span>
            </label>
          {/if}

          {#if esTarjeta}
            <div class="fechas">
              <label>
                <span class="etiqueta">Día de corte</span>
                <input type="number" min="1" max="31" bind:value={diaCorte} />
              </label>
              <label>
                <span class="etiqueta">Día límite de pago</span>
                <input type="number" min="1" max="31" bind:value={diaLimite} />
              </label>
            </div>
            <p class="nota-fechas">
              No son lo mismo. El <strong>corte</strong> cierra el periodo; la
              <strong>fecha límite</strong> es hasta cuándo puedes pagar sin
              intereses. Comprar justo después del corte te da casi un mes más.
              En Nu son 10 días entre una y otra.
            </p>
          {/if}

          {#if [TIPO_CUENTA.AHORROS, TIPO_CUENTA.CORRIENTE].includes(tipo)}
            <label class="casilla">
              <input type="checkbox" bind:checked={exentaGmf} />
              <span>
                <strong>Es mi cuenta exenta del 4x1000</strong>
                <span class="ayuda">
                  Solo puedes tener una marcada. Te deja mover hasta
                  $18.330.900 al mes sin pagar el impuesto.
                </span>
              </span>
            </label>
          {/if}

          <div class="botones">
            <button class="secundario" onclick={() => (paso = 2)}>Atrás</button>
            <button class="principal" disabled={!nombre.trim() || guardando} onclick={confirmar}>
              {guardando ? 'Guardando…' : 'Guardar cuenta'}
            </button>
          </div>
        {/if}

        <button class="cancelar" onclick={reiniciar}>Cancelar</button>
      </div>
    {/if}
  </div>
{/if}

<style>
  .pantalla {
    position: fixed;
    inset: 0;
    z-index: var(--z-hoja);
    background: var(--bg-canvas);
    overflow-y: auto;
    max-width: var(--w-app);
    margin-inline: auto;
    animation: entrar var(--d-normal) var(--ease-entrada);
  }
  @keyframes entrar {
    from { opacity: 0; transform: translateX(16px) }
    to   { opacity: 1; transform: translateX(0) }
  }

  header {
    position: sticky;
    top: 0;
    z-index: var(--z-pegajoso);
    display: flex;
    align-items: center;
    gap: var(--e-2);
    padding: calc(var(--e-3) + var(--safe-t)) var(--gutter) var(--e-3);
    background: var(--bg-canvas);
    border-bottom: 1px solid var(--border);
  }
  .volver {
    width: var(--tap);
    height: var(--tap);
    display: grid;
    place-items: center;
    font-size: var(--t-xl);
    border-radius: var(--r-full);
    margin-left: calc(var(--e-3) * -1);
  }
  h1 { font-size: var(--t-lg); font-weight: var(--w-semibold); flex: 1 }

  .agregar {
    font-size: var(--t-sm);
    font-weight: var(--w-semibold);
    color: var(--acento);
    padding: var(--e-2) var(--e-3);
    border-radius: var(--r-full);
    background: var(--acento-suave);
    min-height: var(--tap-min);
  }

  .contenido {
    padding: var(--e-4) var(--gutter) calc(var(--e-16) + var(--safe-b));
    display: flex;
    flex-direction: column;
    gap: var(--e-4);
  }

  section { display: flex; flex-direction: column; gap: var(--e-2) }

  h2 {
    font-size: var(--t-xs);
    font-weight: var(--w-semibold);
    text-transform: uppercase;
    letter-spacing: var(--track-wide);
    color: var(--ink-3);
  }

  /* ── Fila de cuenta ── */
  .cuenta {
    display: grid;
    grid-template-columns: 40px 1fr auto;
    gap: var(--e-3);
    align-items: center;
    padding: var(--e-3);
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: var(--r-lg);
  }
  .icono {
    width: 40px;
    height: 40px;
    display: grid;
    place-items: center;
    border-radius: var(--r-full);
    background: var(--bg-sunken);
    font-size: var(--t-lg);
  }
  .datos { min-width: 0 }
  .nombre {
    font-weight: var(--w-medium);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .tipo {
    font-size: var(--t-xs);
    color: var(--ink-3);
    display: flex;
    gap: var(--e-1);
    align-items: center;
  }
  .marca {
    font-size: var(--t-2xs);
    color: var(--acento);
    background: var(--acento-suave);
    padding: 0 4px;
    border-radius: var(--r-xs);
  }

  .cifras { text-align: right; display: flex; flex-direction: column }
  .saldo { font-weight: var(--w-semibold); white-space: nowrap }
  .saldo.deuda { color: var(--peligro) }
  .cupo { font-size: var(--t-2xs); color: var(--ink-3); white-space: nowrap }

  .cuenta { width: 100%; text-align: left; transition: transform var(--d-instant) var(--ease-salida) }
  .cuenta:active { transform: scale(0.99) }

  .dialogo {
    display: flex;
    flex-direction: column;
    gap: var(--e-2);
    padding: var(--e-4);
    background: var(--bg-surface);
    border: 1px solid var(--peligro);
    border-radius: var(--r-lg);
  }
  .dialogo h3 { font-size: var(--t-lg); font-weight: var(--w-semibold) }
  .dialogo .explica { font-size: var(--t-sm); color: var(--ink-2); line-height: var(--lh-loose) }
  .peligroso {
    padding: var(--e-3);
    border-radius: var(--r-md);
    color: var(--peligro);
    background: var(--peligro-suave);
    font-weight: var(--w-medium);
    min-height: var(--tap);
  }

  .sugerencia {
    font-size: var(--t-sm);
    color: var(--ink-2);
    line-height: var(--lh-loose);
    padding: var(--e-3);
    background: var(--bg-sunken);
    border-radius: var(--r-md);
  }

  /* ── Agregar ── */
  .lista-opciones { display: flex; flex-direction: column; gap: var(--e-2) }

  .opcion {
    display: flex;
    align-items: center;
    gap: var(--e-3);
    padding: var(--e-3);
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: var(--r-md);
    text-align: left;
    min-height: 56px;
    transition: border-color var(--d-rapida) var(--ease-estandar),
                transform var(--d-instant) var(--ease-salida);
  }
  .opcion:active { transform: scale(0.99) }
  .opcion:hover { border-color: var(--border-strong) }

  .punto-color {
    width: 12px;
    height: 12px;
    border-radius: var(--r-full);
    flex: 0 0 auto;
  }
  .texto-opcion { display: flex; flex-direction: column; gap: 2px; min-width: 0 }
  .titulo-opcion { font-weight: var(--w-medium) }
  .ayuda-opcion { font-size: var(--t-xs); color: var(--ink-3) }

  label { display: flex; flex-direction: column; gap: var(--e-1) }
  .etiqueta { font-size: var(--t-sm); font-weight: var(--w-medium) }

  input[type='text'],
  input[type='number'] {
    padding: var(--e-3);
    font-size: var(--t-base);
    background: var(--bg-surface);
    border: 1px solid var(--border-strong);
    border-radius: var(--r-md);
    min-height: var(--tap);
  }

  .ayuda { font-size: var(--t-xs); color: var(--ink-3); line-height: var(--lh-normal) }

  .fechas { display: grid; grid-template-columns: 1fr 1fr; gap: var(--e-3) }
  .nota-fechas {
    font-size: var(--t-xs);
    color: var(--ink-2);
    line-height: var(--lh-loose);
    padding: var(--e-3);
    background: var(--alerta-suave);
    border-radius: var(--r-md);
  }

  .casilla { flex-direction: row; gap: var(--e-3); align-items: flex-start }
  .casilla input { width: 20px; height: 20px; margin-top: 2px }
  .casilla span { display: flex; flex-direction: column; gap: 2px }

  .botones { display: flex; gap: var(--e-2); margin-top: var(--e-2) }
  .secundario {
    padding: var(--e-3) var(--e-4);
    border-radius: var(--r-md);
    background: var(--bg-sunken);
    font-weight: var(--w-medium);
    min-height: var(--tap);
  }
  .principal {
    flex: 1;
    padding: var(--e-3);
    border-radius: var(--r-md);
    background: var(--acento);
    color: var(--on-acento);
    font-weight: var(--w-semibold);
    min-height: var(--tap);
  }
  .principal:disabled { opacity: 0.4 }

  .cancelar {
    font-size: var(--t-sm);
    color: var(--ink-3);
    padding: var(--e-3);
    min-height: var(--tap);
  }
</style>
